import React from "react";
import type { AspectId } from "./frame";
import { getFrame } from "./frame";
import type { Theme } from "./theme";
import type { Storyboard } from "./director";
import { Stage } from "./Stage";
import { beatToScene } from "./beats";
import type { BrandKit } from "./brand";
import { planSfx, scheduleSfx } from "./sfx";
import { beatToStoryScene, beatToStorybookScene, beatToOverlayScene, deriveStorySet } from "./story";
import type { CharacterSpec } from "./visuals-character";
import { buildFilmTimeline, layersAt } from "./transitions";

/**
 * Anivaze v4 — export the storyboard to a REAL video file.
 *
 * Rasterises the deterministic film frame-by-frame (SVG → <img> → canvas) and
 * captures the canvas with MediaRecorder → a downloadable .webm. Vector in, real
 * video out, no server round-trip. Browser-only; guarded for support. (Server-side
 * HD export via WebCodecs/ffmpeg is a later phase — PLAN §14/§18.)
 */

/** Lazily load react-dom/server so it stays out of the main bundle (export-only). */
type RTSM = (el: React.ReactElement) => string;
let _rtsm: RTSM | null = null;
async function getRenderer(): Promise<RTSM> {
  if (!_rtsm) _rtsm = (await import("react-dom/server")).renderToStaticMarkup as RTSM;
  return _rtsm;
}

export interface ExportOptions {
  fps?: number;
  /** Cap width for reasonable file size/perf; height scales to keep aspect. */
  maxWidth?: number;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
  /**
   * Per-beat narration clips (beat.id → object-URL + duration). When present,
   * each clip is scheduled on the film timeline at its beat's start and mixed
   * into the recording — the export ships WITH the voiceover. Beat durs are
   * clip+0.55s while transition overlap caps at 0.5s, so clips never collide.
   */
  voiceover?: Map<string, { url: string; duration: number }> | null;
  /**
   * Pacing ramp (STAKEHOLDER 2026-07-19): playback speed applied AT export.
   * 1 = as-authored; 0.92 = slow to 92% so a slightly-fast 55s of content
   * relaxes into ~60s (calmer voice + motion). Slows BOTH video and voice.
   * NOTE: audio uses AudioBufferSource.playbackRate, which shifts pitch by
   * ~(1-speed) — an 8% slow drops pitch ~8% (deeper/calmer, NOT chipmunk).
   * Pitch-PRESERVED slow needs an ffmpeg.wasm `atempo` pass (PLATFORM-ADDITIONS
   * §7 / PRODUCT-CHAT-EDIT §6) — the later upgrade. Clamped to [0.5, 1].
   */
  speed?: number;
  /**
   * Preferred container. "mp4" records H.264/AAC when the browser supports it
   * (universal: iOS / YouTube / socials), else transparently falls back to
   * WebM. "webm" (default) keeps the original vp9/opus path.
   */
  container?: "webm" | "mp4";
  /** Optional brand kit — header/footer/watermark/logo baked into the export. */
  brand?: BrandKit | null;
  /** Storyline mode: render each beat as the cast delivering it on a stage. */
  story?: boolean;
  cast?: CharacterSpec[];
  /** Storybook sub-mode: render each beat as a full-bleed scene still + captions. */
  storybook?: boolean;
  /** Green-screen overlay: chroma green + captions + voice — for keying elsewhere. */
  overlay?: boolean;
  /** Synthesized SFX layer (whoosh/pop/scratch). Default ON; false disables. */
  sfx?: boolean;
}

export function isExportSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (window as any).MediaRecorder !== "undefined" &&
    typeof HTMLCanvasElement.prototype.captureStream === "function"
  );
}

function pickMime(withAudio: boolean, preferMp4: boolean): string {
  // With an audio track the mime must name an audio codec, or some browsers
  // silently drop the track from the recording.
  const mp4 = withAudio
    ? ["video/mp4;codecs=avc1.42E01E,mp4a.40.2", "video/mp4;codecs=avc1,mp4a", "video/mp4"]
    : ["video/mp4;codecs=avc1.42E01E", "video/mp4;codecs=avc1", "video/mp4"];
  const webm = withAudio
    ? ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]
    : ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];
  // Prefer the requested container; the other stays as a transparent fallback so
  // export never fails just because a browser can't record the preferred one.
  const candidates = preferMp4 ? [...mp4, ...webm] : [...webm, ...mp4];
  const MR = (window as any).MediaRecorder;
  for (const m of candidates) {
    if (MR && typeof MR.isTypeSupported === "function" && MR.isTypeSupported(m)) return m;
  }
  return "video/webm";
}

/** File extension matching a recorder mime — the caller names the download with it. */
export function extForMime(mime: string): "mp4" | "webm" {
  return mime.startsWith("video/mp4") ? "mp4" : "webm";
}

/** Serialize a single film layer (one beat) to a sized SVG string. */
function layerSVG(rtsm: RTSM, sb: Storyboard, theme: Theme, aspect: AspectId, index: number, localTime: number, W: number, H: number, brand?: BrandKit | null, story?: boolean, cast?: CharacterSpec[], storybook?: boolean, overlay?: boolean): string {
  // Same story-wide set as FilmPlayer — preview === export.
  const meta = { index, total: sb.beats.length, format: sb.format, set: story ? deriveStorySet(sb.beats) : undefined };
  const scene = overlay
    ? beatToOverlayScene(sb.beats[index], meta)
    : storybook
    ? beatToStorybookScene(sb.beats[index], meta)
    : story && cast && cast.length ? beatToStoryScene(sb.beats[index], meta, cast) : beatToScene(sb.beats[index], meta);
  const svg = rtsm(React.createElement(Stage, { aspect, theme, scene, t: localTime, motion: false, brand }));
  // Ensure intrinsic raster size for the <img> path — and the SVG namespace:
  // a standalone data-URI SVG without xmlns is invalid XML and rasterizes to
  // onerror in every browser (the "frame rasterization failed" bug).
  const ns = svg.includes("xmlns=") ? "" : `xmlns="http://www.w3.org/2000/svg" `;
  return svg.replace(/^<svg /, `<svg ${ns}width="${W}" height="${H}" `);
}

function loadImage(svg: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("frame rasterization failed"));
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  });
}

/**
 * Render the storyboard to a .webm Blob. Runs in real time (≈ the video's own
 * length) because MediaRecorder captures the canvas live.
 */
export async function exportFilmWebM(
  sb: Storyboard,
  theme: Theme,
  aspect: AspectId,
  opts: ExportOptions = {},
): Promise<Blob> {
  if (!isExportSupported()) throw new Error("Video export isn't supported in this browser. Try Chrome or Edge.");

  const { fps = 30, maxWidth = 720, onProgress, signal } = opts;
  // Pacing ramp: slow playback to `speed` (≤1). Film time advances slower than
  // wall-clock, so a 55s film records as ~60s of calmer video + voice.
  const speed = Math.max(0.5, Math.min(1, opts.speed ?? 1));
  const frame = getFrame(aspect);
  const scale = Math.min(1, maxWidth / frame.w);
  const W = Math.round(frame.w * scale);
  const H = Math.round(frame.h * scale);

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create a canvas context for export.");

  const rtsm = await getRenderer();
  const timeline = buildFilmTimeline(sb);
  const kind = theme.motion === "snappy" ? "push" : "fade";
  const total = timeline.total;

  const stream = canvas.captureStream(fps);

  // ── audio mix (optional): per-beat narration clips + the synthesized SFX
  // layer, routed through one MediaStream track so the recording ships with
  // the full soundtrack (sfx.ts — whoosh under cuts, pops/scratches on beats) ──
  const wantSfx = opts.sfx !== false;
  let audioCtx: AudioContext | null = null;
  let audioDest: MediaStreamAudioDestinationNode | null = null;
  const audioBuffers = new Map<string, AudioBuffer>();
  if ((wantSfx || (opts.voiceover && opts.voiceover.size > 0)) && typeof AudioContext !== "undefined") {
    try {
      audioCtx = new AudioContext();
      if (opts.voiceover && opts.voiceover.size > 0) {
        const decoded = await Promise.allSettled(
          [...opts.voiceover.entries()].map(async ([id, a]) => {
            const buf = await (await fetch(a.url)).arrayBuffer();
            return [id, await audioCtx!.decodeAudioData(buf)] as const;
          }),
        );
        decoded.forEach((r) => { if (r.status === "fulfilled") audioBuffers.set(r.value[0], r.value[1]); });
      }
      audioDest = audioCtx.createMediaStreamDestination();
    } catch {
      // A failed audio pipeline must never kill the export — fall back to silent.
      try { await audioCtx?.close(); } catch { /* already closed */ }
      audioCtx = null;
      audioDest = null;
      audioBuffers.clear();
    }
  }

  const mimeType = pickMime(audioDest !== null, opts.container === "mp4");
  const recStream = audioDest
    ? new MediaStream([...stream.getVideoTracks(), ...audioDest.stream.getAudioTracks()])
    : stream;
  const recorder = new (window as any).MediaRecorder(recStream, { mimeType, videoBitsPerSecond: 6_000_000 });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e: any) => {
    if (e.data && e.data.size) chunks.push(e.data);
  };
  const finished = new Promise<Blob>((resolve, reject) => {
    recorder.onerror = (e: any) => reject(new Error(`recorder error: ${e?.error?.message ?? "unknown"} (${mimeType})`));
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      if (blob.size < 4096) {
        reject(new Error(`recorder produced no data (${blob.size} bytes, ${mimeType}) — this browser's encoder failed; try desktop Chrome or Edge`));
      } else {
        resolve(blob);
      }
    };
  });

  const drawAt = async (t: number) => {
    ctx.clearRect(0, 0, W, H);
    const layers = layersAt(timeline, t, kind);
    for (const l of layers) {
      const img = await loadImage(layerSVG(rtsm, sb, theme, aspect, l.index, l.localTime, W, H, opts.brand, opts.story, opts.cast, opts.storybook, opts.overlay));
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, l.opacity));
      // Same camera-settle the live player applies (centered scale) — preview === export.
      const s = l.scale ?? 1;
      ctx.drawImage(img, l.dx * W + (W - W * s) / 2, (H - H * s) / 2, W * s, H * s);
      ctx.restore();
    }
  };

  await drawAt(0); // paint first frame before recording starts
  recorder.start();

  // Schedule each beat's narration clip at its window start on the SAME clock
  // the visual loop uses. Clips can't collide: beat dur = clip + 0.55s while
  // transition overlap caps at 0.5s.
  const sources: AudioBufferSourceNode[] = [];
  if (audioCtx && audioDest) {
    try { await audioCtx.resume(); } catch { /* keep going — video still records */ }
    const a0 = audioCtx.currentTime + 0.02;
    for (const w of timeline.windows) {
      const buf = audioBuffers.get(w.beat.id);
      if (!buf) continue;
      const src = audioCtx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = speed;   // pacing ramp: slow the voice in lock-step with the video
      src.connect(audioDest);
      src.start(a0 + w.start / speed);  // its window start, on the slowed clock
      sources.push(src);
    }
    // SFX layer — the same pure plan the live preview fires (preview === export).
    if (wantSfx) {
      try {
        for (const ev of planSfx(timeline, theme.id)) {
          scheduleSfx(audioCtx, audioDest, ev.type, a0 + ev.at / speed, speed);
        }
      } catch { /* SFX must never break an export */ }
    }
  }

  try {
    const start = performance.now();
    await new Promise<void>((resolve, reject) => {
      const step = async () => {
        if (signal?.aborted) return reject(new DOMException("Export cancelled", "AbortError"));
        const t = ((performance.now() - start) / 1000) * speed;  // film time (slowed by the pacing ramp)
        if (t >= total) {
          await drawAt(total - 0.001);
          onProgress?.(1);
          return resolve();
        }
        await drawAt(t);
        onProgress?.(t / total);
        requestAnimationFrame(() => void step());
      };
      requestAnimationFrame(() => void step());
    });

    recorder.stop();
    return await finished;
  } finally {
    if (recorder.state !== "inactive") { try { recorder.stop(); } catch { /* already stopped */ } }
    for (const s of sources) { try { s.stop(); } catch { /* already ended */ } }
    if (audioCtx) { try { await audioCtx.close(); } catch { /* already closed */ } }
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
